/**
 * Delivery Slot Utilities
 * Shared utility functions for time calculations and formatting
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { formatReadableSlotDay } from "./cptAuditTooltipUtils";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST_TIMEZONE = "Asia/Kolkata";
const DAYS_FORMAT_THRESHOLD = 6;
const CLOCK_HHMM_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)$/;
const NOON_HOUR = 12;
const MERIDIEM_AM = "AM";
const MERIDIEM_PM = "PM";

// Default Quick ETA options in minutes
export const DEFAULT_ETA_MINS = [20, 25, 45, 60, 120, 150, 200, 220, 250, 300];

/**
 * Convert minutes to readable format
 * @example minutesToHours(90) => "1:30 hrs"
 * @example minutesToHours(60) => "1 hrs"
 * @example minutesToHours(30) => "30 mins"
 */
export function minutesToHours(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours === 0) return `${remainingMinutes} mins`;
  if (remainingMinutes > 0) return `${hours}:${remainingMinutes} hrs`;
  return `${hours} hrs`;
}

/**
 * Calculate future date for datetime-local input
 * Returns IST datetime string formatted for datetime-local input (YYYY-MM-DDTHH:mm)
 */
export function calculateFutureDate(minutes: number): string {
  const futureIST = dayjs().tz(IST_TIMEZONE).add(minutes, "minutes");
  return futureIST.format("YYYY-MM-DDTHH:mm");
}

// /**
//  * Format CPT for display
//  * @example formatCPTDisplay("2024-01-15T14:30:00Z") => "15 JAN, 2:30 PM"
//  */
// export function formatCPTDisplay(isoString: string): string {
//   const date = new Date(isoString);
//   const day = date.getDate();
//   const month = date.toLocaleDateString("en-IN", { month: "short" });
//   const time = date.toLocaleTimeString("en-IN", {
//     hour: "numeric",
//     minute: "2-digit",
//     hour12: true,
//   });
//   return `${day} ${month}, ${time}`.toUpperCase();
// }

/**
 * Parse quick ETA minutes from config string
 * Falls back to DEFAULT_ETA_MINS if parsing fails
 */
export function parseQuickEtaMins(quickEtasMins?: string): number[] {
  if (!quickEtasMins) return DEFAULT_ETA_MINS;
  const parsed = quickEtasMins
    .split(",")
    .map((m) => parseInt(m.trim()))
    .filter((m) => !isNaN(m));
  return parsed.length > 0 ? parsed : DEFAULT_ETA_MINS;
}

/** Converts a 24-hour `HH:mm` clock to `1:15 PM`. Invalid input is returned unchanged. */
export function formatClockTimeAmPm(hhmm: string): string {
  const match = CLOCK_HHMM_PATTERN.exec(hhmm.trim());
  if (!match) {
    return hhmm;
  }
  const hours24 = Number(match[1]);
  const minutes = match[2];
  const meridiem = hours24 >= NOON_HOUR ? MERIDIEM_PM : MERIDIEM_AM;
  const hours12 =
    hours24 % NOON_HOUR === 0 ? NOON_HOUR : hours24 % NOON_HOUR;
  return `${hours12}:${minutes} ${meridiem}`;
}

function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";

  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

function formatOrdinalDateTime(date: dayjs.Dayjs): string {
  return `${date.date()}${getOrdinalSuffix(date.date())} ${date.format(
    "MMM",
  )}, ${date.format("HH:mm")}`;
}

/** e.g. "26th Aug, 2026 09:30 AM" */
export function formatOrdinalDateTimeWithYear(isoString: string): string {
  if (!isoString) return "";
  const date = dayjs(isoString).tz(IST_TIMEZONE);
  if (!date.isValid()) return "";
  return `${date.date()}${getOrdinalSuffix(date.date())} ${date.format(
    "MMM, YYYY hh:mm A",
  )}`;
}

/**
 * Format date/time as "Today, 7:15 PM" or "Tomorrow, 7:15 PM" or "17th Jul, 18:30"
 */
export function formatSmartDateTime(isoString: string): string {
  if (!isoString) return "";

  const date = dayjs(isoString).tz(IST_TIMEZONE);
  if (!date.isValid()) return "";

  const today = dayjs().tz(IST_TIMEZONE).startOf("day");
  const tomorrow = today.add(1, "day");
  const inputDate = date.startOf("day");

  const timeStr = date.format("h:mm A");

  if (inputDate.isSame(today)) {
    return `Today, ${timeStr}`;
  } else if (inputDate.isSame(tomorrow)) {
    return `Tomorrow, ${timeStr}`;
  } else {
    return formatOrdinalDateTime(date);
  }
}

/**
 * Calculate days from today to a given date
 */
function daysFromToday(dateString: string): number {
  const date = dayjs(dateString).tz(IST_TIMEZONE);
  if (!date.isValid()) return 0;

  const today = dayjs().tz(IST_TIMEZONE).startOf("day");
  const targetDate = date.startOf("day");
  const diffDays = targetDate.diff(today, "day");
  return Math.max(0, diffDays);
}

/**
 * Format CPT range display
 * - If range is up to 6 days away: shows smart date/time format
 * - If range goes beyond 6 days: shows "X - Y Days" format
 */
export function formatCPTRange(cptMin: string, cptMax: string): string {
  if (!cptMin || !cptMax) return "";

  const minDays = daysFromToday(cptMin);
  const maxDays = daysFromToday(cptMax);

  if (minDays <= DAYS_FORMAT_THRESHOLD && maxDays <= DAYS_FORMAT_THRESHOLD) {
    if (cptMin === cptMax) return formatSmartDateTime(cptMax);
    return `${formatSmartDateTime(cptMin)} - ${formatSmartDateTime(cptMax)}`;
  }

  if (minDays === maxDays) {
    return `${minDays} Days`;
  }

  return `${minDays} - ${maxDays} Days`;
}

function formatGaraazSlotDayPrefix(isoString: string): string {
  const date = dayjs(isoString).tz(IST_TIMEZONE);
  if (!date.isValid()) return "";
  const isoDate = date.format("YYYY-MM-DD");
  const todayIso = dayjs().tz(IST_TIMEZONE).format("YYYY-MM-DD");
  return formatReadableSlotDay(isoDate, todayIso);
}

export function parseGaraazSlotNumber(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isInteger(parsed) && Number.isFinite(parsed)
    ? parsed
    : undefined;
}

export function formatGaraazSlotBoundLabel(
  cptIso: string,
  slotNumber: number,
): string {
  const dayPrefix = formatGaraazSlotDayPrefix(cptIso);
  if (!dayPrefix) return `Slot ${slotNumber}`;
  return `${dayPrefix}, Slot ${slotNumber}`;
}

export function formatInvolvedGaraazSlotTimings(
  slots:
    | Array<{ slotId: number; startTime: string; endTime: string }>
    | undefined,
  minSlot?: number,
  maxSlot?: number,
): string {
  const involved = new Set<number>();
  if (minSlot) {
    involved.add(minSlot);
  }
  if (maxSlot) {
    involved.add(maxSlot);
  }
  const lines: string[] = [];
  for (const slot of slots ?? []) {
    if (involved.size > 0 && !involved.has(slot.slotId)) {
      continue;
    }
    lines.push(
      `Slot ${slot.slotId}: ${formatClockTimeAmPm(slot.startTime)} - ${formatClockTimeAmPm(slot.endTime)}`,
    );
  }
  return lines.join("\n");
}

export function formatGaraazSlotRangeLabel(
  cptMin: string,
  cptMax: string,
  minGaraazSlot: number,
  maxGaraazSlot: number,
): string {
  const minLabel = formatGaraazSlotBoundLabel(cptMin, minGaraazSlot);
  const minDay = formatGaraazSlotDayPrefix(cptMin);
  const maxDay = formatGaraazSlotDayPrefix(cptMax);
  if (minDay && minDay === maxDay) {
    if (minGaraazSlot === maxGaraazSlot) {
      return minLabel;
    }
    return `${minDay}, Slot ${minGaraazSlot} - Slot ${maxGaraazSlot}`;
  }
  return `${minLabel} - ${formatGaraazSlotBoundLabel(cptMax, maxGaraazSlot)}`;
}
